import * as React from "react";
import { toast } from "react-toastify";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import { SoftwareRelease, SoftwareReleaseClient, SoftwareReleaseService } from "@app/software-releases";
import moment from "moment";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { isEqual } from "lodash";
import { ModalLink } from "@app/shared/Modals";
import { FontAwesome, QesIcons } from "@app/shared/icons/qesIcons";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { SoftwareReleaseDetailEditPanelContent } from "@app/admin/software-releases/SoftwareReleaseDetailEditPanelContent";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface SoftwareReleaseClientTableData {
  client: string;
  hold: string;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  client: {
    displayText: "Client",
    testId: "client-name",
  },
  hold: {
    displayText: "Hold Software Release?",
    testId: "hold-software-release",
  },
  dateReleasedToClient: {
    displayText: "Date Released to Client",
    testId: "date-released-to-client"
  }
  /* oxlint-enable sort-keys */
};

interface EditSoftwareReleaseProps {
  softwareReleaseId: string;
}
type PropsType = RouteComponentProps<EditSoftwareReleaseProps>;

export const SoftwareReleaseDetail: React.FC<React.PropsWithChildren<PropsType>> = (props: PropsType) => {

  const softwareReleaseService = new SoftwareReleaseService();

  const [error] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [isDirty, setIsDirty] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [softwareReleaseId, setSoftwareReleaseId] = React.useState<number>();
  const [softwareRelease, setSoftwareRelease] = React.useState<SoftwareRelease>();
  const [releaseVersion, setReleaseVersion] = React.useState("-1");
  const [softwareReleaseClientsTableData, setSoftwareReleaseClientsTableData] = React.useState<SoftwareReleaseClientTableData[]>([]);

  const [selectedSoftwareReleaseClientIds, setSelectedSoftwareReleaseClientIds] = React.useState<number[]>([]);
  const initialSoftwareReleaseClientIds = React.useRef<number[]>([]);

  const qasSoftwareReleaseBreadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.QasSoftwareReleases];

  React.useEffect(() => {
    setSoftwareReleaseId(parseInt(props.match.params.softwareReleaseId, 10))
  });

  React.useEffect(() => {
    if (softwareReleaseId !== undefined) {
      loadSoftwareRelease(softwareReleaseId);
    }
  }, [softwareReleaseId]);

  const loadSoftwareRelease = async (id: number) => {
    setLoading(true);

    const sr = await softwareReleaseService.getById(id)

    setSoftwareRelease(sr);
    setReleaseVersion(`${sr.releaseYear}.${sr.releaseQuarter}`);

    if (sr.dateReleased) {
      softwareReleaseService.getEligibleClients(id).then(clients => {

        const releasedClientIds = clients.filter(c => c.isReleased).map(c => c.clientId).sort();

        setSoftwareReleaseClientsTableData(buildTableData(clients));

        initialSoftwareReleaseClientIds.current = {
          ...releasedClientIds
        };

        setSelectedSoftwareReleaseClientIds(releasedClientIds);
      });
    }

    setLoading(false);
  }

  const buildTableData = (data: SoftwareReleaseClient[]): SoftwareReleaseClientTableData[] => {

    return data.map<SoftwareReleaseClientTableData>((src) => {
      /* oxlint-disable sort-keys */
      return {
        client: src.clientName,
        hold: src.holdSoftwareRelease ? "Y" : "N",
        [wafflesKeyColumnName]: src.clientId.toString(),
      };
      /* oxlint-enable sort-keys */
    });
  }

  const breadcrumbs: (string | Breadcrumb)[] = React.useMemo(() => ([
    ...qasSoftwareReleaseBreadcrumbs,
    { text: loading ? "Loading...": releaseVersion }
  ]), [releaseVersion, loading]);

  const handleDelete = () => {
    setSubmitting(true);

    softwareReleaseService.delete(softwareReleaseId).then(() => {
      toast(<span>Software Release successfully deleted!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem deleting this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  const handleRelease = () => {
    setSubmitting(true);

    softwareReleaseService.release(softwareReleaseId).then(() => {
      toast(<span>Software Release successfully updated!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem releasing this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  const handleSubmit = () => {
    setSubmitting(true);

    softwareReleaseService.updateClientsByReleaseId(softwareReleaseId, selectedSoftwareReleaseClientIds).then(() => {
      toast(<span>Software Release successfully updated!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem updating this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  const handleSelectedClientsChange = (clientIds: string[]) => {
    setSelectedSoftwareReleaseClientIds(clientIds.map(cid => parseInt(cid)).sort());
  }

  React.useEffect(() => {
    const currentIsDirty = !isEqual(selectedSoftwareReleaseClientIds, Object.values(initialSoftwareReleaseClientIds.current));
    setIsDirty(currentIsDirty);

  }, [selectedSoftwareReleaseClientIds])

  const setRowStyle = (row: SoftwareReleaseClientTableData) => {
    return row.hold === "Y" ? "warning" : "";
  }

  const handleDownload = async () => {
    await softwareReleaseService.getEmailListCsv(softwareReleaseId);
  }

  const handleEditSubmit = async (updatedSoftwareRelease: SoftwareRelease) => {
    setLoading(true);
    await softwareReleaseService.update(softwareReleaseId, updatedSoftwareRelease);
    await loadSoftwareRelease(softwareReleaseId);
    setLoading(false);
  }

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <Page documentTitle="Software Release Detail - Admin">
      <Breadcrumbs items={breadcrumbs} />
      <PageInner>
        <PageHeader title={`QAS Software Release ${releaseVersion}`} iconName={FontAwesome.Laptop} disableHR={false} >
          <PageHeaderLeft>
            <RoleRestricted allowedRoles={[Roles.SOFTWARE_LICENSE_ADMIN]}>
              <span hidden={!softwareRelease?.dateReleased}>
                <QesIcon iconName={QesIcons.PencilV1} className="mb-1 me-1" isDecorative />
                <FlyoutLink
                  classNames="me-2"
                  content={{
                    body:
                      <SoftwareReleaseDetailEditPanelContent
                        softwareRelease={softwareRelease}
                        onSubmit={handleEditSubmit}
                      />,
                    title: "Edit Software Release",
                  }}
                  size="40"
                  testId="update-release__edit-flyout"
                >
                  Edit
                </FlyoutLink>
              </span>
              <QESButton
                qesTestId="update-release__download"
                className="software-release__header-btn-link"
                qesType="secondary"
                onClick={() => { handleDownload(); }}
                hidden={!softwareRelease?.dateReleased}
              >
                <i className="fa fa-download me-1" />
                Download Email List
              </QESButton>
            </RoleRestricted>
          </PageHeaderLeft>
          <PageHeaderRight>
            {softwareReleaseId !== undefined && !softwareRelease?.dateReleased && (
              <ModalLink
                content={{
                  body: "Are you sure you want to delete this Software Release?",
                  cancelButtonText: "Cancel",
                  confirmButtonAction: handleDelete,
                  confirmButtonText: "Delete",
                  confirmButtonType: "primary-danger",
                  title: "Delete Software Release",
                }}
                disabled={!!softwareRelease?.dateReleased}
                testId="software-releases__delete-modal"
                classNames="qes-button-secondary danger"
                hideBodyScroll={true}
              >
                Delete
              </ModalLink>
            )}
          </PageHeaderRight>
        </PageHeader>
        <div className="row mt-3">
          <div className="col-10">
            {loading && <div className="mt-3"><LoadingSpinner /></div>}
            {!(loading || submitting) && (
              <>
                <PageSection>
                  {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
                  <div className="ms-3">
                    <div className="row">
                      <label className="qes-text-input__label me-1 col-auto px-0">Download URL:</label>{softwareRelease?.releasePath || ""}
                    </div>
                    <div className="row align-middle">
                      <label className="qes-text-input__label me-1 col-auto px-0">Date Released:</label>{!!softwareRelease?.dateReleased ? moment.utc(softwareRelease?.dateReleased).local().format("L") : "(not released)"}
                      {softwareReleaseId !== undefined && !softwareRelease?.dateReleased && (
                        <ModalLink
                          content={{
                            body: "Are you sure you want to release this Software Release?",
                            cancelButtonText: "Cancel",
                            confirmButtonAction: handleRelease,
                            confirmButtonText: "Release",
                            title: "Release Software Release",
                          }}
                          disabled={!!softwareRelease?.dateReleased}
                          testId="software-releases__release-modal"
                          classNames="qes-button-secondary pt-0 col-auto"
                          hideBodyScroll={true}
                        >
                          Release This Version
                        </ModalLink>
                      )}
                    </div>
                    {!!softwareRelease?.dateReleased && softwareReleaseClientsTableData &&
                      (
                        <>
                          <div className="row mt-3">
                            <label className="qes-text-input__label px-0">Select Clients for This Release:</label>
                          </div>
                          <Waffles<SoftwareReleaseClientTableData>
                            columns={columnConfig}
                            data={softwareReleaseClientsTableData}
                            enablePagination={false}
                            fullWidth={true}
                            passThroughSorting={true}
                            testId="software-release-clients-table"
                            showCheckboxes={true}
                            selectedKeys={selectedSoftwareReleaseClientIds.map(cid => cid.toString())}
                            handleCheckboxChange={handleSelectedClientsChange}
                            rowDecorator={setRowStyle}
                          />
                        </>
                      )}
                  </div>
                </PageSection>
              </>
            )}
          </div>
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={!submitting && <Link className="qes-button qes-button-secondary danger" to="/admin/software-releases">Cancel</Link>}
        rightSide={!submitting && !!softwareRelease?.dateReleased &&
          <>
            <QESButton
              qesType="primary"
              disabled={!isDirty}
              onClick={handleSubmit}
              qesTestId="update_release__submit"
            >
              Update Client Release Status
            </QESButton>
          </>
        }
      />
    </Page >
  );
}
